import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from './client';
import type { Facets, Paginated, RecipeDetail, RecipeSummary } from './types';

export type RecipeFilters = {
  search?: string;
  season?: string;
  protein?: string;
  /** Include the swap variants a card prints, which are hidden by default. */
  variants?: boolean;
  /** Show only the recipes ruled out for good — the way back to a block. */
  blocked?: boolean;
};

function toQuery(filters: RecipeFilters): string {
  const params = new URLSearchParams();
  if (filters.search) params.set('search', filters.search);
  if (filters.season) params.set('season', filters.season);
  if (filters.protein) params.set('protein', filters.protein);
  if (filters.variants) params.set('variants', 'include');
  if (filters.blocked) params.set('blocked', 'only');
  const query = params.toString();
  return query ? `?${query}` : '';
}

export function useRecipes(filters: RecipeFilters) {
  return useQuery({
    queryKey: ['recipes', filters],
    queryFn: () => api<Paginated<RecipeSummary>>(`/recipes/${toQuery(filters)}`),
  });
}

export function useRecipe(id: string | undefined, servings?: number) {
  const suffix = servings ? `?servings=${servings}` : '';
  return useQuery({
    queryKey: ['recipe', id, servings],
    queryFn: () => api<RecipeDetail>(`/recipes/${id}/${suffix}`),
    enabled: Boolean(id),
  });
}

export function useFacets(variants = false) {
  // Counted over whatever the listing is showing, so the numbers beside each
  // filter match what choosing it returns.
  const suffix = variants ? '?variants=include' : '';
  return useQuery({
    queryKey: ['facets', variants],
    queryFn: () => api<Facets>(`/recipes/facets/${suffix}`),
    staleTime: 60 * 60 * 1000,
  });
}

/** Offer a blocked dinner again.

    The other half of the "don't suggest this again" box on a swap. It lives
    on the recipe rather than beside the box, because by the time a cook wants
    it back the week they blocked it in is long gone. */
export function useUnblockRecipe() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (recipeId: number) =>
      api<RecipeDetail>(`/recipes/${recipeId}/unblock/`, { method: 'POST' }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['recipe'] });
      queryClient.invalidateQueries({ queryKey: ['recipes'] });
      queryClient.invalidateQueries({ queryKey: ['facets'] });
      // Eligible again for whatever gets planned next.
      queryClient.invalidateQueries({ queryKey: ['plans'] });
    },
  });
}
